import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { EllipsisIcon } from 'lucide-react';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';

/**
 * Описание колонки read-only таблицы секций карточки типа сущности.
 */
export type OntologySectionColumn<Row> = {
  /** Стабильный ключ колонки */
  key: string;
  /** Заголовок колонки (он же подпись в меню управления колонками) */
  header: string;
  /** Рендер ячейки строки */
  cell: (row: Row) => ReactNode;
  /** Есть ли в строке непустое значение — основа авто-скрытия пустых колонок */
  hasValue: (row: Row) => boolean;
  /** Доп. классы ячеек и заголовка колонки (например, `whitespace-nowrap` для «Код») */
  cellClassName?: string;
};

type Props<Row> = {
  /** Заголовок секции-таблицы (выводится в шапке карточки) */
  title: string;
  /** Колонки в порядке отображения */
  columns: OntologySectionColumn<Row>[];
  /** Строки таблицы */
  rows: Row[];
  /** Ключ строки для React */
  getRowKey: (row: Row) => string;
};

/**
 * Read-only таблица секций карточки типа сущности (атрибуты схемы, типы связей).
 *
 * Раскладка:
 * - пустые колонки (ни в одной строке нет значения) скрываются автоматически;
 * - все присутствующие колонки видны по умолчанию; пользователь может скрыть любую
 *   через меню управления колонками (кнопка `⋯` в правом углу шапки таблицы);
 * - ширина колонок — по содержимому (`table-auto`), длинный текст переносится.
 */
export const OntologySectionTable = <Row,>({ title, columns, rows, getRowKey }: Props<Row>) => {
  const { t } = useTranslation('ontology');

  // Колонки, у которых есть непустое значение хотя бы в одной строке (авто-скрытие пустых)
  const presentColumns = useMemo(() => columns.filter((col) => rows.some((row) => col.hasValue(row))), [columns, rows]);

  // Ключи колонок, скрытых пользователем через меню управления колонками
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  const toggleColumn = (key: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const visibleColumns = presentColumns.filter((col) => !hiddenKeys.has(col.key));

  return (
    <Card>
      <CardHeader className="items-center border-b">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardAction className="self-center">
          <Popover>
            <IconButton
              size="icon"
              className="size-7 rounded-md text-muted-foreground hover:bg-primary-hover hover:text-primary"
              label={t('columnsSettings')}
              icon={<EllipsisIcon className="size-4" />}
              trigger={PopoverTrigger}
            />
            <PopoverContent align="end" className="w-56">
              <div className="flex flex-col gap-2">
                {presentColumns.map((col) => (
                  <Checkbox
                    key={col.key}
                    label={col.header}
                    value={!hiddenKeys.has(col.key)}
                    onChange={() => toggleColumn(col.key)}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </CardAction>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full table-auto border-collapse text-left text-sm">
          <thead>
            <tr className="border-b">
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={cn('px-4 py-2 text-left font-medium text-muted-foreground', col.cellClassName)}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={getRowKey(row)} className="border-b align-top last:border-0">
                {visibleColumns.map((col) => (
                  <td key={col.key} className={cn('px-4 py-2 align-top', col.cellClassName)}>
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
